import React, { useState, useEffect } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import {
  colorPaletteOutline,
  imageOutline,
  imagesOutline,
  brushOutline,
  keyOutline,
  copyOutline,
  trashOutline,
  addOutline,
  checkmarkOutline,
} from 'ionicons/icons';
import { Modal, ModalSection, ModalRow } from './ui/Modal/Modal';
import { TextInput } from './ui/TextInput';
import { Button } from './ui/Button';
import {
  type Dashboard,
  type SlideshowConfig,
  updateDashboard,
  getLocalPhotoAlbums,
  type LocalPhotoAlbum,
} from '../services/dashboard';
import {
  generateDashboardAccessCode,
  listDashboardAccessCodes,
  revokeDashboardAccessCode,
  formatCode,
  type DashboardAccessCode,
} from '../services/quickAccess';
import SlideshowConfigModal from './SlideshowConfigModal';

interface DashboardSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  dashboard: Dashboard | null;
  householdId: string;
  onSave?: (updatedDashboard: Dashboard) => void;
}

type BackgroundType = 'color' | 'gradient' | 'image' | 'slideshow';

interface BackgroundOption {
  type: BackgroundType;
  icon: string;
  label: string;
  description: string;
}

const BACKGROUND_OPTIONS: BackgroundOption[] = [
  {
    type: 'color',
    icon: colorPaletteOutline,
    label: 'Solid Color',
    description: 'Single background color',
  },
  {
    type: 'gradient',
    icon: brushOutline,
    label: 'Gradient',
    description: 'Color gradient background',
  },
  {
    type: 'image',
    icon: imageOutline,
    label: 'Image',
    description: 'Static background image',
  },
  {
    type: 'slideshow',
    icon: imagesOutline,
    label: 'Slideshow',
    description: 'Rotating photo backgrounds',
  },
];

// Preset gradients
const PRESET_GRADIENTS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  'linear-gradient(180deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
];

// Preset colors
const PRESET_COLORS = [
  '#1a1a2e',
  '#16213e',
  '#0f3460',
  '#1a1a1a',
  '#2d3436',
  '#636e72',
  '#b2bec3',
  '#dfe6e9',
];

const DashboardSettingsModal: React.FC<DashboardSettingsModalProps> = ({
  isOpen,
  onClose,
  dashboard,
  householdId,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  const [backgroundType, setBackgroundType] = useState<BackgroundType>('color');
  const [backgroundColor, setBackgroundColor] = useState('#1a1a2e');
  const [backgroundGradient, setBackgroundGradient] = useState(PRESET_GRADIENTS[0]);
  const [backgroundImage, setBackgroundImage] = useState('');
  const [slideshowConfig, setSlideshowConfig] = useState<SlideshowConfig | null>(null);
  const [localAlbums, setLocalAlbums] = useState<LocalPhotoAlbum[]>([]);
  const [saving, setSaving] = useState(false);

  // Slideshow config modal state
  const [showSlideshowModal, setShowSlideshowModal] = useState(false);

  // Access codes state
  const [accessCodes, setAccessCodes] = useState<DashboardAccessCode[]>([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [newCodeName, setNewCodeName] = useState('');
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && dashboard) {
      setName(dashboard.name);
      setDescription(dashboard.description || '');
      setIcon(dashboard.icon || '');
      setBackgroundType(dashboard.background_type || 'color');

      if (dashboard.background_type === 'color' && dashboard.background_value) {
        setBackgroundColor(dashboard.background_value);
      }
      if (dashboard.background_type === 'gradient' && dashboard.background_value) {
        setBackgroundGradient(dashboard.background_value);
      }
      if (dashboard.background_type === 'image' && dashboard.background_value) {
        setBackgroundImage(dashboard.background_value);
      }
      if (dashboard.background_slideshow_config) {
        setSlideshowConfig(dashboard.background_slideshow_config);
      }

      // Load local albums
      loadLocalAlbums();

      // Load access codes
      loadAccessCodes(dashboard.id);

      // Clear any previous errors
      setCodeError(null);
    }
  }, [isOpen, dashboard]);

  const loadAccessCodes = async (dashboardId: string) => {
    setLoadingCodes(true);
    try {
      const codes = await listDashboardAccessCodes(dashboardId);
      setAccessCodes(codes);
    } finally {
      setLoadingCodes(false);
    }
  };

  const handleGenerateCode = async () => {
    if (!dashboard) return;

    setGeneratingCode(true);
    setCodeError(null);
    try {
      const result = await generateDashboardAccessCode(dashboard.id, newCodeName || undefined);
      if (result.success) {
        setNewCodeName('');
        // Reload codes
        await loadAccessCodes(dashboard.id);
      } else {
        setCodeError(result.error || 'Failed to generate code');
        console.error('Generate code error:', result.error);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setCodeError(errorMessage);
      console.error('Generate code exception:', err);
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleRevokeCode = async (codeId: string) => {
    const result = await revokeDashboardAccessCode(codeId);
    if (result.success && dashboard) {
      await loadAccessCodes(dashboard.id);
    }
  };

  const handleCopyCode = async (code: string, codeId: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCodeId(codeId);
      setTimeout(() => setCopiedCodeId(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const loadLocalAlbums = async () => {
    const albums = await getLocalPhotoAlbums(householdId);
    setLocalAlbums(albums);
  };

  const getBackgroundValue = (): string => {
    switch (backgroundType) {
      case 'color':
        return backgroundColor;
      case 'gradient':
        return backgroundGradient;
      case 'image':
        return backgroundImage;
      case 'slideshow':
        return ''; // Slideshow uses config, not value
      default:
        return '';
    }
  };

  const handleSave = async () => {
    if (!dashboard) return;

    setSaving(true);
    try {
      const updates: Partial<Dashboard> = {
        name,
        description: description || undefined,
        icon: icon || undefined,
        background_type: backgroundType,
        background_value: getBackgroundValue(),
      };

      if (backgroundType === 'slideshow' && slideshowConfig) {
        updates.background_slideshow_config = slideshowConfig;
      }

      const success = await updateDashboard(dashboard.id, updates);
      if (success) {
        onSave?.({
          ...dashboard,
          ...updates,
        });
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSlideshowConfigSave = (config: SlideshowConfig) => {
    setSlideshowConfig(config);
    setShowSlideshowModal(false);
  };

  const getSlideshowSummary = () => {
    if (!slideshowConfig?.album_id) return 'Not configured';

    const source = slideshowConfig.source === 'google_photos' ? 'Google Photos' : 'Local Album';
    return `${source} • ${slideshowConfig.interval_seconds}s interval`;
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        header={{ title: 'Dashboard Settings' }}
        footer={{
          primaryAction: {
            label: 'Save',
            onClick: handleSave,
            loading: saving,
          },
          secondaryAction: {
            label: 'Cancel',
            onClick: onClose,
          },
        }}
      >
        {/* Basic Info */}
        <ModalSection title="Basic Info">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <TextInput
              label="Name"
              value={name}
              onChange={setName}
              placeholder="Dashboard name"
            />
            <TextInput
              label="Description"
              value={description}
              onChange={setDescription}
              placeholder="Optional description"
            />
            <TextInput
              label="Icon"
              value={icon}
              onChange={setIcon}
              placeholder="Emoji icon (e.g., 🏠)"
            />
          </div>
        </ModalSection>

        {/* Background Type */}
        <ModalSection title="Background Type">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 12,
          }}>
            {BACKGROUND_OPTIONS.map(option => (
              <button
                key={option.type}
                onClick={() => setBackgroundType(option.type)}
                style={{
                  padding: '12px',
                  borderRadius: 10,
                  border: backgroundType === option.type
                    ? '2px solid var(--ion-color-primary)'
                    : '2px solid var(--ion-border-color)',
                  background: backgroundType === option.type
                    ? 'rgba(var(--ion-color-primary-rgb), 0.1)'
                    : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'all 0.15s ease',
                }}
              >
                <IonIcon
                  icon={option.icon}
                  style={{
                    fontSize: 24,
                    color: backgroundType === option.type
                      ? 'var(--ion-color-primary)'
                      : 'var(--ion-color-medium)',
                  }}
                />
                <span style={{
                  fontSize: 13,
                  fontWeight: backgroundType === option.type ? 600 : 500,
                  color: backgroundType === option.type
                    ? 'var(--ion-text-color)'
                    : 'var(--ion-color-medium)',
                }}>
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        </ModalSection>

        {/* Background Options based on type */}
        {backgroundType === 'color' && (
          <ModalSection title="Color">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 8,
              }}>
                {PRESET_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setBackgroundColor(color)}
                    style={{
                      width: '100%',
                      aspectRatio: '1',
                      borderRadius: 8,
                      backgroundColor: color,
                      border: backgroundColor === color
                        ? '3px solid var(--ion-color-primary)'
                        : '2px solid var(--ion-border-color)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  />
                ))}
              </div>
              <TextInput
                label="Custom Color"
                value={backgroundColor}
                onChange={setBackgroundColor}
                placeholder="#1a1a2e"
              />
            </div>
          </ModalSection>
        )}

        {backgroundType === 'gradient' && (
          <ModalSection title="Gradient">
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 8,
            }}>
              {PRESET_GRADIENTS.map((gradient, idx) => (
                <button
                  key={idx}
                  onClick={() => setBackgroundGradient(gradient)}
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    borderRadius: 8,
                    background: gradient,
                    border: backgroundGradient === gradient
                      ? '3px solid var(--ion-color-primary)'
                      : '2px solid var(--ion-border-color)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                />
              ))}
            </div>
          </ModalSection>
        )}

        {backgroundType === 'image' && (
          <ModalSection title="Image URL">
            <TextInput
              label="Image URL"
              value={backgroundImage}
              onChange={setBackgroundImage}
              placeholder="https://example.com/image.jpg"
            />
            {backgroundImage && (
              <div style={{
                marginTop: 12,
                width: '100%',
                height: 120,
                borderRadius: 8,
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                border: '1px solid var(--ion-border-color)',
              }} />
            )}
          </ModalSection>
        )}

        {backgroundType === 'slideshow' && (
          <ModalSection title="Slideshow Settings">
            <div style={{
              padding: 16,
              background: 'var(--ion-card-background)',
              borderRadius: 10,
              border: '1px solid var(--ion-border-color)',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div>
                  <div style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--ion-text-color)',
                  }}>
                    Photo Slideshow
                  </div>
                  <div style={{
                    fontSize: 13,
                    color: 'var(--ion-color-medium)',
                    marginTop: 4,
                  }}>
                    {getSlideshowSummary()}
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => setShowSlideshowModal(true)}
                >
                  Configure
                </Button>
              </div>
            </div>
          </ModalSection>
        )}

        {/* Access Codes Section */}
        <ModalSection title="Kiosk Access Codes" divider={false}>
          <div style={{
            padding: 16,
            background: 'var(--ion-card-background)',
            borderRadius: 10,
            border: '1px solid var(--ion-border-color)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
            }}>
              <IonIcon
                icon={keyOutline}
                style={{ fontSize: 20, color: 'var(--ion-color-primary)' }}
              />
              <span style={{ fontSize: 13, color: 'var(--ion-color-medium)' }}>
                Generate codes to allow kiosk devices to display this dashboard
              </span>
            </div>

            {/* Generate new code */}
            <div style={{
              display: 'flex',
              gap: 8,
              marginBottom: codeError ? 8 : 16,
            }}>
              <div style={{ flex: 1 }}>
                <TextInput
                  value={newCodeName}
                  onChange={setNewCodeName}
                  placeholder="Code name (optional)"
                  disabled={generatingCode}
                />
              </div>
              <Button
                variant="primary"
                size="small"
                onClick={handleGenerateCode}
                disabled={generatingCode}
                iconStart={generatingCode ? undefined : addOutline}
              >
                {generatingCode ? <IonSpinner name="crescent" style={{ width: 16, height: 16 }} /> : 'Generate'}
              </Button>
            </div>

            {/* Error message */}
            {codeError && (
              <div style={{
                padding: '8px 12px',
                marginBottom: 16,
                background: 'rgba(var(--ion-color-danger-rgb), 0.15)',
                borderRadius: 6,
                color: 'var(--ion-color-danger)',
                fontSize: 13,
              }}>
                {codeError}
              </div>
            )}

            {/* Existing codes list */}
            {loadingCodes ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                padding: 24,
              }}>
                <IonSpinner name="crescent" />
              </div>
            ) : accessCodes.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: 24,
                color: 'var(--ion-color-medium)',
                fontSize: 13,
              }}>
                No access codes generated yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {accessCodes.map(code => (
                  <div
                    key={code.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 14px',
                      background: 'rgba(var(--ion-color-primary-rgb), 0.08)',
                      borderRadius: 8,
                    }}
                  >
                    {/* Code display */}
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: 18,
                        fontWeight: 700,
                        letterSpacing: 2,
                        color: 'var(--ion-text-color)',
                      }}>
                        {formatCode(code.code)}
                      </div>
                      <div style={{
                        fontSize: 12,
                        color: 'var(--ion-color-medium)',
                        marginTop: 2,
                      }}>
                        {code.name || 'Unnamed'}
                        {code.use_count > 0 && ` • Used ${code.use_count}x`}
                      </div>
                    </div>

                    {/* Copy button */}
                    <button
                      onClick={() => handleCopyCode(code.code, code.id)}
                      style={{
                        padding: 8,
                        border: 'none',
                        borderRadius: 6,
                        background: copiedCodeId === code.id
                          ? 'var(--ion-color-success)'
                          : 'var(--ion-color-step-100)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <IonIcon
                        icon={copiedCodeId === code.id ? checkmarkOutline : copyOutline}
                        style={{
                          fontSize: 18,
                          color: copiedCodeId === code.id
                            ? 'white'
                            : 'var(--ion-text-color)',
                        }}
                      />
                    </button>

                    {/* Revoke button */}
                    <button
                      onClick={() => handleRevokeCode(code.id)}
                      style={{
                        padding: 8,
                        border: 'none',
                        borderRadius: 6,
                        background: 'rgba(var(--ion-color-danger-rgb), 0.15)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <IonIcon
                        icon={trashOutline}
                        style={{
                          fontSize: 18,
                          color: 'var(--ion-color-danger)',
                        }}
                      />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Instructions */}
            <div style={{
              marginTop: 16,
              padding: 12,
              background: 'rgba(var(--ion-color-medium-rgb), 0.1)',
              borderRadius: 8,
              fontSize: 12,
              color: 'var(--ion-color-medium)',
            }}>
              <strong>To use:</strong> On the kiosk device, go to <code style={{
                background: 'rgba(0,0,0,0.2)',
                padding: '2px 6px',
                borderRadius: 4,
              }}>/access</code> and enter the code.
            </div>
          </div>
        </ModalSection>
      </Modal>

      {/* Slideshow Config Modal */}
      <SlideshowConfigModal
        isOpen={showSlideshowModal}
        onClose={() => setShowSlideshowModal(false)}
        config={slideshowConfig}
        onSave={handleSlideshowConfigSave}
        householdId={householdId}
        localAlbums={localAlbums}
      />
    </>
  );
};

export default DashboardSettingsModal;
